import React, { useEffect, useState } from 'react';
import { Row, Typography, message } from 'antd';
import { Form, Input, Button } from 'antd';
import Title from 'antd/lib/typography/Title';
import Paragraph from 'antd/lib/typography/Paragraph';
import Link from 'antd/lib/typography/Link';
import { useHistory } from 'react-router-dom';
import { InternalNamePath } from 'antd/lib/form/interface';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import PasswordStrength, { ZXCVBNFeedback } from 'zxcvbn';
import {
  CloseCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import Text from 'antd/lib/typography';

export interface SignUpFormValue {
  Email: string;
  password: string;
  verificationCode: string;
}

class SignUpInterface {
  verificationMode!: boolean;
  submitting!: boolean;
  onSubmit!: (values: SignUpFormValue) => Promise<void>;
}

const SignUp: React.FC<SignUpInterface> = ({
  verificationMode,
  submitting,
  onSubmit
}) => {
  const [input, setInput] = useState('');
  const [passwordSecurityLevel, changePasswordSecurityLevel] =
    useState<number>();
  const history = useHistory();

  const onSubmitFailed = (
    errorInfo: ValidateErrorEntity<SignUpFormValue>
  ): void => {
    errorInfo.errorFields.map(
      (item: { name: InternalNamePath; errors: string[] }) => {
        return message.error(item.errors[0]);
      }
    );
    console.log('Failed:', errorInfo);
  };

  return (
    <>
      <Row justify="center">
        {verificationMode ? (
          <Title level={3}>Verify your account</Title>
        ) : (
          <Title level={3}>Start a free account</Title>
        )}
      </Row>
      {verificationMode && (
        <Paragraph style={{ marginBottom: '20px' }}>
          Please check your emails for the verification code and copy it here:
        </Paragraph>
      )}
      <Form
        layout="vertical"
        name="basic"
        initialValues={{
          remember: true
        }}
        onFinish={onSubmit}
        onFinishFailed={onSubmitFailed}
      >
        <Form.Item
          label="Email"
          name="Email"
          data-cy="Email"
          hidden={verificationMode}
          rules={[
            {
              required: true,
              message: 'Please input your email!'
            }
          ]}
        >
          <Input size="large" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="Password"
          name="password"
          data-cy="Password"
          hidden={verificationMode}
          rules={[
            {
              required: true,
              message: 'Please input your password!'
            }
          ]}
        >
          <Input.Password
            value={input}
            size="large"
            onChange={(e) => {
              setInput(e.target.value);
            }}
            style={{ width: '100%' }}
          />
        </Form.Item>
        {input.length !== 0 && !verificationMode && (
          <Form.Item style={{ maxWidth: '100%' }}>
            <PasswordStrengthIndicator
              input={input}
              onLevelChange={changePasswordSecurityLevel}
              settings={{
                colorScheme: {
                  levels: [
                    '#ff4033',
                    '#fe940d',
                    '#ffd908',
                    '#cbe11d',
                    '#6ecc3a'
                  ],
                  noLevel: 'lightgrey'
                },
                height: 10,
                alwaysVisible: false,
                width: 400
              }}
            />
            {input ? <PasswordWarningsAndSuggestions input={input} /> : null}
          </Form.Item>
        )}

        {verificationMode ? (
          <>
            <Form.Item
              label="Verification Code"
              name="verificationCode"
              data-cy="verificationCode"
              rules={[
                {
                  required: true,
                  message: 'Please input the verification code!'
                }
              ]}
            >
              <Input size="large" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                data-cy="submit"
                size="large"
                block
                loading={submitting}
              >
                Submit
              </Button>
            </Form.Item>
          </>
        ) : (
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              data-cy="signUp"
              size="large"
              block
              loading={submitting}
              disabled={
                passwordSecurityLevel && passwordSecurityLevel >= 3
                  ? false
                  : true
              }
            >
              Sign Up
            </Button>
          </Form.Item>
        )}
      </Form>
      <Paragraph
        type="secondary"
        style={{ maxWidth: 360, textAlign: 'center' }}
      >
        By signing up, you agree to our{' '}
        <Typography.Link
          type="secondary"
          underline
          href="https://www.notificationapi.com/terms-of-service"
        >
          Terms of Service
        </Typography.Link>
        , and agree to receive important updates and marketing messages that you
        can opt out of.
      </Paragraph>
      <Row justify="center">
        <Text>
          Already have an account?{' '}
          <Link onClick={(): void => history.push('/signin')}>Sign in</Link>
        </Text>
      </Row>
    </>
  );
};

interface PasswordStrengthIndicatorProps extends LevelChangeProps {
  input: string;
  settings: PasswordInputSettings;
}
interface PasswordWarningsAndSuggestionsProps {
  input: string;
}
export const PasswordStrengthIndicator = ({
  input,
  settings,
  onLevelChange,
  onFeedbackChange
}: PasswordStrengthIndicatorProps): JSX.Element | null => {
  // Calculate level

  const level = React.useMemo(() => {
    return input.length === 0 ? -1 : PasswordStrength(input).score;
  }, [input]);
  // Calculate indicators
  const indicators: React.ReactElement[] = React.useMemo(() => {
    const ind = [];
    for (let i = 0; i < 5; i++) {
      const color =
        i <= level
          ? settings.colorScheme.levels[level]
          : settings.colorScheme.noLevel;
      ind.push(
        <div
          key={`indicator-${i}`}
          style={getIndicatorStyle(color, settings.height)}
        />
      );
    }

    return ind;
  }, [level, settings]);
  useEffect(() => {
    onLevelChange?.(level);
    onFeedbackChange?.(PasswordStrength(input).feedback);
  }, [level, input, onLevelChange, onFeedbackChange]);

  if (!settings.alwaysVisible && level < 0) {
    return null;
  }

  return (
    <div style={getWrapperStyle(settings.height, settings.width)}>
      {indicators}
    </div>
  );
};
export const PasswordWarningsAndSuggestions = ({
  input
}: PasswordWarningsAndSuggestionsProps): JSX.Element | null => {
  // create warning and suggestions
  const warningAndSuggestions: React.ReactElement[] = React.useMemo(() => {
    const ind = [];
    console.log(PasswordStrength(input).feedback);
    if (PasswordStrength(input).feedback.warning) {
      ind.push(
        <div key={`warning`}>
          <Paragraph>
            <CloseCircleOutlined style={{ color: 'red' }} />
            {`  ${PasswordStrength(input).feedback.warning}`}
          </Paragraph>
        </div>
      );
    }
    PasswordStrength(input).feedback.suggestions.map((suggestion, index) => {
      ind.push(
        <div key={`suggestion-${index}`}>
          <Paragraph>
            <ExclamationCircleOutlined style={{ color: 'orange' }} />{' '}
            {`  ${suggestion}`}{' '}
          </Paragraph>
        </div>
      );
      return null;
    });

    return ind;
  }, [input]);

  return <div>{warningAndSuggestions}</div>;
};
function getWrapperStyle(height: number, width: number) {
  return {
    top: '0',
    lineHeight: height + 'px',
    width
  };
}

function getIndicatorStyle(color: string, height: number) {
  return {
    display: 'inline-block',
    width: '20%',
    backgroundColor: color,
    height: height + 'px'
  };
}

export interface PasswordInputSettings {
  colorScheme: ColorScheme;
  height: number;
  alwaysVisible: boolean;
  width: number;
}

export interface ColorScheme {
  levels: string[];
  noLevel: string;
}

export default SignUp;
interface LevelChangeProps {
  onLevelChange?: (newLevel: number) => void;
  onFeedbackChange?: (feedback: ZXCVBNFeedback) => void;
}
